import React from 'react';
import { Box, Card, CardContent, Typography, Chip, useTheme, Button } from '@mui/material';
import { lighten, darken } from '@mui/material/styles';
import { LocationOn, AccessTime, Groups, DateRange } from '@mui/icons-material';
import Parent from '../parentInterface.ts';

interface ParentInfoCardProps {
  parent: Parent | null;
  onRefreshChildren?: () => void;
  isRefreshingChildren?: boolean;
  refreshDisabled?: boolean;
  refreshStatusText?: string;
}

const ParentInfoCard: React.FC<ParentInfoCardProps> = ({
  parent,
  onRefreshChildren,
  isRefreshingChildren = false,
  refreshDisabled = false,
  refreshStatusText = '',
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  if (!parent) {
    return (
      <Card sx={{ width: '100%', mb: 1, borderRadius: '8px' }}>
        <CardContent sx={{ py: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Loading parent information...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const formatTime = (timeString: string) => {
    return timeString || 'N/A';
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.getDate() + ' ' + date.toLocaleString('en-US', { month: 'short' });
  };

  const formatLastChildSync = (value?: string | null): string => {
    if (!value) return 'Never';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString();
  };

  // Combined name with agent
  const displayName = parent.agent_name ? `${parent.name} - ${parent.agent_name}` : parent.name;

  // Theme-aware colors
  const cardBg = isDarkMode
    ? `linear-gradient(145deg, ${darken(theme.palette.background.paper, 0.2)} 0%, ${darken(theme.palette.background.paper, 0.4)} 100%)`
    : `linear-gradient(145deg, ${lighten(theme.palette.background.paper, 0.05)} 0%, ${theme.palette.background.paper} 100%)`;

  const accentGradient = `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`;
  const textPrimaryColor = theme.palette.text.primary;
  const textSecondaryColor = theme.palette.text.secondary;
  const iconColor = theme.palette.mode === 'dark' ? '#9e9e9e' : '#666666';

  return (
    <Card
      sx={{
        width: '100%',
        mb: 1,
        borderRadius: '8px',
        background: cardBg,
        boxShadow: theme.shadows[2],
        position: 'relative',
        height: '45px'
      }}
    >
      {/* Decorative accent strip */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '3px',
        background: accentGradient
      }} />

      <CardContent sx={{ py: 1.5, px: 2 }}>
        {/* Single line with all information */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'nowrap',
          width: '100%'
        }}>
          {/* Left side with name and details */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            maxWidth: 'calc(100% - 70px)',
            overflow: 'hidden'
          }}>
            {/* Name */}
            <Typography
              variant="subtitle1"
              component="div"
              fontWeight="bold"
              sx={{
                color: textPrimaryColor,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: '100px',
                maxWidth: '100%'
              }}
            >
              {displayName || 'Unnamed Tour'}
            </Typography>

            {/* Location */}
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flexShrink: 1 }}>
              <LocationOn sx={{ mr: 0.5, fontSize: 15, color: iconColor }} />
              <Typography variant="body2" sx={{
                color: textSecondaryColor,
                fontSize: '0.8rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {parent.location_name || 'N/A'}
              </Typography>
            </Box>

            {/* Date */}
            <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
              <DateRange sx={{ mr: 0.5, fontSize: 15, color: iconColor }} />
              <Typography variant="body2" sx={{ color: textSecondaryColor, fontSize: '0.8rem' }}>
                {formatDate(parent.date)}
              </Typography>
            </Box>

            {/* Time */}
            <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
              <AccessTime sx={{ mr: 0.5, fontSize: 15, color: iconColor }} />
              <Typography variant="body2" sx={{ color: textSecondaryColor, fontSize: '0.8rem' }}>
                {formatTime(parent.time_from)} - {formatTime(parent.time_to)}
              </Typography>
            </Box>

            {/* Size */}
            <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
              <Groups sx={{ mr: 0.5, fontSize: 15, color: iconColor }} />
              <Typography variant="body2" sx={{ color: textSecondaryColor, fontSize: '0.8rem' }}>
                {parent.size || 0}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              size="small"
              label={`ID: ${parent.booking_id || 'N/A'}`}
              sx={{
                height: '20px',
                fontSize: '0.65rem',
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                color: theme.palette.text.secondary,
                flexShrink: 0
              }}
            />
            <Chip
              size="small"
              label={`Last refreshed: ${formatLastChildSync(parent.last_child_sync_at)}`}
              sx={{
                height: '20px',
                fontSize: '0.65rem',
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                color: theme.palette.text.secondary,
                flexShrink: 0
              }}
            />
            <Button
              size="small"
              variant="contained"
              onClick={onRefreshChildren}
              disabled={isRefreshingChildren || refreshDisabled}
            >
              {isRefreshingChildren ? 'Refreshing...' : 'Refresh Child Bookings'}
            </Button>
          </Box>
        </Box>
        {refreshStatusText ? (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
            {refreshStatusText}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default ParentInfoCard;
